import { createApp, h, reactive } from 'vue'
import { pinia } from '@/pinia'

export function defineVueAppElement(
    tag: string,
    component: any,
    install?: (app: ReturnType<typeof createApp>) => void
) {
    // Idempotent: the entry module can be re-evaluated when a lazy schema chunk loads,
    // and customElements.define throws if the tag is already registered.
    if (customElements.get(tag)) return

    customElements.define(tag, class extends HTMLElement {
        private app: ReturnType<typeof createApp> | null = null
        private props = reactive<Record<string, any>>({})

        connectedCallback() {
            if (this.app) return

            Object.assign(this.props, getPropsFromAttributes(this))

            Object.keys(this.props).forEach(key => {
                Object.defineProperty(this, key, {
                    get: () => this.props[key],
                    set: (val) => { this.props[key] = val }
                })
            })

            const root = { render: () => h(component, this.props) }
            this.app = createApp(root)
            this.app.use(pinia)

            install?.(this.app)
            this.app.mount(this)
        }

        disconnectedCallback() {
            this.app?.unmount()
            this.app = null
        }
    })
}

function getPropsFromAttributes(el: HTMLElement) {
    const props: Record<string, any> = {}
    for (const attr of Array.from(el.attributes)) {
        const key = attr.name.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
        props[key] = coerce(attr.value)
    }
    return props
}

function coerce(value: string) {
    if (value === '') return true
    if (value === 'true' || value === 'false') return value === 'true'
    if (!Number.isNaN(Number(value))) return Number(value)
    try { return JSON.parse(value) } catch { return value }
}
